import { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React from 'react';
import { FormattedNumber } from 'react-intl';

import { PrizeCurrency } from '@swf/casino';
import CurrencySymbol from '@swf/components/CurrencySymbol/CurrencySymbol';
import ibIcon from '@swf/core/assets/casino/instant-bonus.png';
import FSBIcon from '@swf/core/assets/icons/free-spins.svg';
import type { Prize, PrizeDetail, Standing } from '@swf/core/types/CasinoRaces';
import { PrizeType } from '@swf/core/types/CasinoRaces';

import {
  TXTCLI_Player,
  TXTCLI_Prize,
  TXTCLI_Rank,
  TXTCLI_Score,
  TXTMOB_You
} from './Table.messages.json';
import styles from './Table.scss';

export enum ColumnName {
  Rank = 'rank',
  Name = 'name',
  Score = 'score',
  Amount = 'amount'
}

const Columns = {
  [ColumnName.Rank]: TXTCLI_Rank,
  [ColumnName.Name]: TXTCLI_Player,
  [ColumnName.Score]: TXTCLI_Score,
  [ColumnName.Amount]: TXTCLI_Prize
};

// interface TableEntity extends Standing, Prize {}

export interface Props {
  className?: string;
  prizeDetails: PrizeDetail;
  standings: Standing[];
  userStanding?: Standing;
  medal?: boolean;
  usePosition?: boolean;
}

const Table: React.FC<Props> = ({
  className = '',
  standings,
  prizeDetails,
  userStanding,
  medal = true
}) => {
  if (!standings || standings.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <table className={classNames(className, styles.table, { [styles.medal]: medal })}>
        <thead className={styles.tableHead}>
          <tr>
            <Translation tagName="th" {...TXTCLI_Rank} />
            <Translation tagName="th" {...TXTCLI_Player} />
            <Translation tagName="th" {...TXTCLI_Score} />
            <th
              className={classNames(
                styles.prizes,
                { [styles.ibPad]: prizeDetails.prizeType === PrizeType.IB },
                { [styles.fsbPad]: prizeDetails.prizeType === PrizeType.FSB },
                {
                  [styles.cashPad]: [PrizeType.CASH, PrizeType.NONE].includes(
                    prizeDetails.prizeType
                  )
                }
              )}
            >
              <Translation tagName="div" {...TXTCLI_Prize} />
              <div
                className={classNames({
                  [styles.currency]: prizeDetails.prizeType !== PrizeType.NONE
                })}
              >
                {prizeDetails.prizeType === PrizeType.IB && (
                  <img className={styles.ibIcon} src={ibIcon} alt="" />
                )}
                {prizeDetails.prizeType === PrizeType.FSB && <FSBIcon className={styles.fsbIcon} />}
                {prizeDetails.prizeType === PrizeType.CASH && (
                  <>
                    (
                    <CurrencySymbol
                      currency={prizeDetails ? prizeDetails.currency : Currency.USDollar}
                      trimSpace
                    />
                    )
                  </>
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {standings.map((standing, index) => (
            <tr
              key={index}
              className={classNames(styles.row, {
                [styles.current]: userStanding && userStanding?.rank === standing.rank
              })}
            >
              {Object.keys(Columns).map((key) => {
                const {
                  [key]: value,
                  rank,
                  userRacePrize
                } = standing as unknown as Record<string, string | number | Prize | PrizeType>;
                const rankScore =
                  userStanding && userStanding.rank === rank ? userStanding.score : value;
                switch (key) {
                  case ColumnName.Rank:
                    return <td key={key}>{(value as string) || '–'}</td>;
                  case ColumnName.Name:
                    return userStanding && userStanding?.rank === rank ? (
                      <Translation key={key} tagName="td" {...TXTMOB_You} />
                    ) : (
                      <td key={key}>{value ? (value as string) : '–'}</td>
                    );

                  case ColumnName.Score:
                    return (
                      <td key={key}>
                        {!!rankScore && (rankScore as number) > 0 ? (
                          <FormattedNumber
                            value={rankScore as number}
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                          />
                        ) : (
                          '–'
                        )}
                      </td>
                    );
                  case ColumnName.Amount:
                    return (
                      <td key={key}>
                        {!isEmpty(standing) ? <PrizeCurrency {...(userRacePrize as Prize)} /> : '–'}
                      </td>
                    );
                  default:
                    return null;
                }
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
